import { useState, useEffect, useCallback } from 'react';
import { UserProfile, UserProfileInput } from '@/types/user-profile';
import { useSupabaseAuth } from '@/lib/supabase';
import { authenticatedFetch } from '@/lib/services/AuthenticatedFetchService';

/**
 * useUserProfile Hook
 * Manages user profile data with authentication.
 * Fetches and updates user profile via authenticated API calls.
 */
export function useUserProfile() {
  const { session } = useSupabaseAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchProfile = useCallback(async () => {
    if (!session) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch.fetch('/api/user-profile');
      if (!response.ok) {
        throw new Error('Failed to fetch user profile');
      }
      const data = await response.json();
      setProfile(data.profile);
    } catch (e) {
      setError(e as Error);
    } finally {
      setLoading(false);
    }
  }, [session]);

  const updateProfile = useCallback(async (input: Partial<UserProfileInput>) => {
    if (!session) {
      throw new Error('No authentication session');
    }

    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch.fetch('/api/user-profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!response.ok) {
        let errorMessage = 'Failed to update user profile';
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          errorMessage = `Failed to update user profile (Status: ${response.status})`;
        }
        throw new Error(errorMessage);
      }
      const data = await response.json();
      setProfile(data.profile);
      return data.profile;
    } catch (e) {
      setError(e as Error);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return { profile, loading, error, refetch: fetchProfile, updateProfile };
}
